import React from 'react';

const META = {
	email:   'privacy@justknow.co.in',
	company: 'JGS Media and Software Solutions Private Limited',
	appName: 'Justknow',
};

const DeleteAccountPage: React.FC = () => (
	<div style={{
		position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
		overflowY: 'auto', background: '#0a0a1a',
		fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		color: '#fff',
		zIndex: 9000,
	}}>

		{/* Nav */}
		<nav style={{
			position: 'sticky', top: 0, zIndex: 100,
			background: 'rgba(10,10,26,0.9)', backdropFilter: 'blur(20px)',
			borderBottom: '1px solid rgba(255,255,255,0.06)',
			padding: '0 24px',
		}}>
			<div style={{ maxWidth: 800, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<a href='/'>
					<img src='/HeaderLogo2.png' alt='Justknow' style={{ height: 32, objectFit: 'contain' }} />
				</a>
				<a href='/' style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, textDecoration: 'none' }}>← Back to Home</a>
			</div>
		</nav>

		{/* Content */}
		<div style={{ maxWidth: 800, margin: '0 auto', padding: '56px 24px 80px' }}>

			{/* Header */}
			<div style={{ marginBottom: 48 }}>
				<h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-1px', marginBottom: 12 }}>Delete Your Account</h1>
				<p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.7, marginTop: 20 }}>
					You can request the deletion of your <strong style={{ color: '#fff' }}>{META.appName}</strong> account at any time.
					Once your account is deleted, all your personal data will be permanently removed from our systems within <strong style={{ color: '#fff' }}>30 days</strong>,
					subject to any legal retention obligations.
				</p>
				<p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, marginTop: 12 }}>
					Compliant with DPDPA 2023 · IT Act 2000 · IT Rules 2021
				</p>
			</div>

			<Divider />

			{/* In-app option */}
			<Section title="Option 1 — Delete from the App">
				<p>You can delete your account directly from the Justknow app:</p>
				<ol>
					<li>Open the <b>Justknow</b> app and sign in.</li>
					<li>Tap the <b>Profile</b> icon at the bottom of the screen.</li>
					<li>Scroll to the bottom and tap <b>Delete Account</b>.</li>
					<li>Confirm the deletion in the prompt that appears.</li>
				</ol>
				<p>Your account will be scheduled for permanent deletion immediately.</p>
			</Section>

			<Divider />

			{/* Email option */}
			<Section title="Option 2 — Request via Email">
				<p>If you are unable to access the app, you can request account deletion by emailing us:</p>
				<div style={{
					background: 'rgba(77,105,250,0.1)', border: '1px solid rgba(77,105,250,0.3)',
					borderRadius: 12, padding: '20px 24px', margin: '16px 0',
				}}>
					<p style={{ marginBottom: 8 }}>
						<b style={{ color: '#fff' }}>Email:</b>{' '}
						<a href={`mailto:${META.email}?subject=Delete%20Account%20Request`} style={{ color: '#4d69fa' }}>
							{META.email}
						</a>
					</p>
					<p style={{ marginBottom: 8 }}><b style={{ color: '#fff' }}>Subject:</b> Delete Account Request</p>
					<p style={{ marginBottom: 0 }}><b style={{ color: '#fff' }}>Include:</b> The email address or mobile number registered with your account.</p>
				</div>
				<p>We will verify your identity and process your request within <b style={{ color: '#fff' }}>7 business days</b>.</p>
			</Section>

			<Divider />

			{/* What gets deleted */}
			<Section title="What Gets Deleted">
				<p>When your account is deleted, the following data is permanently removed:</p>
				<ul>
					<li>Your name, email address, and mobile number</li>
					<li>Your profile picture and display name</li>
					<li>Your saved bookmarks and reading history</li>
					<li>Your comments and article reactions</li>
					<li>Your notification preferences and device tokens</li>
					<li>Your location preferences (state, district, pincode)</li>
				</ul>
			</Section>

			<Section title="What We May Retain">
				<p>Some data may be retained after deletion where required by applicable Indian law:</p>
				<ul>
					<li>Transaction and payment records (as required under PMLA 2002 and GST regulations)</li>
					<li>Anonymised, aggregated usage statistics that cannot identify you</li>
					<li>Data required to comply with ongoing legal proceedings or government orders</li>
				</ul>
				<p>Retained data is held only for the minimum period required by law and then securely deleted.</p>
			</Section>

			<Divider />

			{/* Timeline */}
			<Section title="Deletion Timeline">
				<ul>
					<li><b style={{ color: '#fff' }}>Immediate:</b> Your account is deactivated and you are signed out of all devices.</li>
					<li><b style={{ color: '#fff' }}>Within 7 days:</b> Your profile and content are removed from the app for all users.</li>
					<li><b style={{ color: '#fff' }}>Within 30 days:</b> All personal data is permanently deleted from our servers and backups.</li>
				</ul>
				<p>Deletion is <b style={{ color: '#fff' }}>irreversible</b>. You will not be able to recover your account or data after this process is complete.</p>
			</Section>

			<Divider />

			{/* Contact */}
			<div style={{ textAlign: 'center', paddingTop: 8 }}>
				<p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
					Questions? Email us at{' '}
					<a href={`mailto:${META.email}`} style={{ color: '#4d69fa' }}>{META.email}</a>
				</p>
			</div>
		</div>

		{/* Footer */}
		<footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.3)', padding: '32px 24px' }}>
			<div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: 16 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<img src='/HeaderLogo2.png' alt='Justknow' style={{ height: 26, objectFit: 'contain' }} />
					<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>Short News App</span>
				</div>
				<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
					© {new Date().getFullYear()} {META.company}. All rights reserved.
				</span>
			</div>
		</footer>

		<style>{`
			* { margin: 0; padding: 0; box-sizing: border-box; }
			::-webkit-scrollbar { width: 6px; }
			::-webkit-scrollbar-track { background: #0a0a1a; }
			::-webkit-scrollbar-thumb { background: rgba(108,93,211,0.5); border-radius: 3px; }
		`}</style>
	</div>
);

// ─── tiny helpers ─────────────────────────────────────────────────────────────

const Divider = () => (
	<hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '40px 0' }} />
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<div style={{ marginBottom: 40 }}>
		<h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14, color: '#fff' }}>{title}</h2>
		<div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.8 }}>
			{children}
		</div>
		<style>{`
			div > div ul, div > div ol { padding-left: 20px; margin: 8px 0; }
			div > div li { margin-bottom: 8px; }
			div > div p  { margin-bottom: 12px; }
		`}</style>
	</div>
);

export default DeleteAccountPage;
